import {
  AgentDelegationStatus,
  type InboxEntry,
  type InboxEntryAgentDelegation,
  isPullRequestUrl,
  PullRequestState,
} from '@this+that/models';

import { isWaitingForReply } from './delegationStatusPresentation';
import {
  isRunActive,
  listVisibleTicketRuns,
  type TicketRun,
} from './listTicketRuns';

/** Lower comes first: the run the header chip and the card band show when a ticket has several. */
export enum RunPriorityTier {
  NEEDS_OWNER = 0,
  FAILED = 1,
  RUNNING = 2,
  QUEUED = 3,
  DONE_WITH_OPEN_PR = 4,
  DONE = 5,
}

function hasOpenPullRequest(delegation: InboxEntryAgentDelegation): boolean {
  return (
    isPullRequestUrl(delegation.prUrl) &&
    delegation.prState !== PullRequestState.MERGED &&
    delegation.prState !== PullRequestState.CLOSED
  );
}

function isWaitingOnOwner(delegation: InboxEntryAgentDelegation): boolean {
  if (isWaitingForReply(delegation)) {
    return false;
  }
  return (
    delegation.status === AgentDelegationStatus.BLOCKED ||
    delegation.status === AgentDelegationStatus.PLAN_READY ||
    delegation.status === AgentDelegationStatus.PAUSED ||
    delegation.pendingApproval != null ||
    delegation.pendingQuestion != null
  );
}

/** Mirrors the run screen's own states: a paused run reads "Needs you" there, so it ranks with
 *  the runs that wait on the owner. A run blocked on someone else's reply keeps working. */
export function resolveRunPriorityTier(
  delegation: InboxEntryAgentDelegation,
): RunPriorityTier {
  if (delegation.status === AgentDelegationStatus.FAILED) {
    return RunPriorityTier.FAILED;
  }
  if (delegation.status === AgentDelegationStatus.DONE) {
    return hasOpenPullRequest(delegation)
      ? RunPriorityTier.DONE_WITH_OPEN_PR
      : RunPriorityTier.DONE;
  }
  if (isWaitingOnOwner(delegation)) {
    return RunPriorityTier.NEEDS_OWNER;
  }
  if (
    delegation.status === AgentDelegationStatus.QUEUED ||
    delegation.status === AgentDelegationStatus.WAITING_FOR_DEVICE
  ) {
    return RunPriorityTier.QUEUED;
  }
  return RunPriorityTier.RUNNING;
}

export function compareRunPriority(a: TicketRun, b: TicketRun): number {
  const tierDiff =
    resolveRunPriorityTier(a.delegation) - resolveRunPriorityTier(b.delegation);
  if (tierDiff !== 0) {
    return tierDiff;
  }
  const startedDiff =
    (b.delegation.startedAt ?? 0) - (a.delegation.startedAt ?? 0);
  if (startedDiff !== 0) {
    return startedDiff;
  }
  return Number(b.isPrimary) - Number(a.isPrimary);
}

/** The run to show for a ticket; the input is expected to be visible runs already. */
export function pickFocusRun(runs: readonly TicketRun[]): TicketRun | null {
  if (runs.length === 0) {
    return null;
  }
  return [...runs].sort(compareRunPriority)[0];
}

export interface TicketRunFocus {
  /** The entry with the picked run standing in as `agentDelegation`, for the single-run resolvers. */
  entry: InboxEntry;
  run: TicketRun | null;
  /** Other visible runs still in flight, the "+N" on the chip and the band. */
  otherActiveCount: number;
}

export function countOtherActiveRuns(
  runs: readonly TicketRun[],
  focus: TicketRun | null,
): number {
  return runs.filter((run) => run !== focus && isRunActive(run.delegation))
    .length;
}

/** What every single-run surface reads: the picked run and how many others are active. With no
 *  visible run the entry passes through unchanged, so attention signals behave as before. */
export function resolveTicketRunFocus(entry: InboxEntry): TicketRunFocus {
  const runs = listVisibleTicketRuns(entry);
  const run = pickFocusRun(runs);
  if (!run) {
    return { entry, run: null, otherActiveCount: 0 };
  }
  return {
    entry: run.isPrimary
      ? entry
      : { ...entry, agentDelegation: run.delegation },
    run,
    otherActiveCount: countOtherActiveRuns(runs, run),
  };
}
